import { ReactElement, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { SpinnerLegacy } from "../../components";
import { useDashboardContext } from "../../components/dashboard/Dashboard.shared";
import { ErrorMessage } from "../../components/errorMessage/ErrorMessage";
import { NavigationBar } from "../../components/navigation/NavigationBar";
import {
  CUSTOMER,
  ORDER_BUYER,
  ORDER_COMMENT,
  ORDER_COMMENT_LIST,
} from "../../constants/routes";
import { useOrder } from "../../hooks/api/v2/order/useOrder";
import { useOrderCommentList } from "../../hooks/api/v2/orderComment/useOrderCommentList";
import { useSupplierRelationshipList } from "../../hooks/api/v2/supplierRelationship/useSupplierRelationshipList";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { SendMessage } from "../order/Message/OrderSendMessage";
import { OrderContentContainer } from "../order/OrderContent/OrderContentContainer";
import { BadPayerWarning } from "../../components/badPayerWarning/BadPayerWarning";

export function OrderCommentPage(): ReactElement {
  const scrollToRef = useRef<HTMLDivElement | null>(null);
  const { isTwoPanel } = useDashboardContext();
  const navigate = useNavigate();
  const { business } = useSellarCurrentBusiness();
  const orderId = useParamAsNumber(ORDER_COMMENT, "orderId");
  const orderQuery = useOrder(orderId);
  const orderData = orderQuery.data;
  const businessTypeSupplier = business?.type === "supplier";
  const supplierRelationshipListQuery = useSupplierRelationshipList(
    {
      limit: 1,
      query: [
        {
          type: "buyerId",
          modifier: "EQUALS",
          value: orderQuery.data?.RetailerId ?? 0,
        },
      ],
    },
    { enabled: !!orderQuery.data && businessTypeSupplier },
  );

  const connection =
    supplierRelationshipListQuery.data?.pages[0]?.[0] ?? undefined;

  const orderCommentListQuery = useOrderCommentList({
    limit: 50,
    sortBy: "createdAt",
    sortDir: "asc",
    query: [
      {
        type: "OrderId",
        modifier: "EQUALS",
        value: orderId,
      },
    ],
  });

  const allDataFetched = orderData && orderCommentListQuery.data;

  useEffect(() => {
    if (allDataFetched) {
      scrollToRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [orderId, allDataFetched]);

  if (orderQuery.isError) {
    return <ErrorMessage>{orderQuery.error.message}</ErrorMessage>;
  }

  if (orderCommentListQuery.isError) {
    return <ErrorMessage>{orderCommentListQuery.error.message}</ErrorMessage>;
  }

  if (!allDataFetched) {
    return <SpinnerLegacy />;
  }

  return (
    <>
      <NavigationBar
        title={
          orderQuery.isLoading
            ? "Loading..."
            : business?.type === "supplier"
              ? orderQuery.data.Retailer.Business.name
              : orderQuery.data.Supplier.Business.name
        }
        onClickTitle={() => {
          if (!connection && businessTypeSupplier) {
            return;
          }

          navigate(
            businessTypeSupplier && connection
              ? CUSTOMER.href({ connectionId: connection.id })
              : ORDER_BUYER.href({
                  supplierIdOrUrlId: orderQuery.data.Supplier.urlId,
                }),
          );
        }}
        leftIcon={"CHEVRON_LEFT"}
        onClickLeftIcon={
          isTwoPanel
            ? undefined
            : () => {
                navigate(ORDER_COMMENT_LIST);
              }
        }
      />
      <OrderContentContainer order={orderData} />
      <div className="sticky bottom-0 mx-auto mt-2 flex flex-col gap-2 bg-gray-100 px-2">
        <div className={"flex flex-col gap-2 pb-5"}>
          <BadPayerWarning
            flaggedAsBadPayer={connection?.Buyer.flaggedAsBadPayer}
            paymentType={orderData.paymentType}
          />
          <SendMessage orderId={orderId} />
        </div>
      </div>
      <div ref={scrollToRef} />
    </>
  );
}
